import { Row } from "@components/Flex";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Caption, Title4 } from "@components/Typography";
import { spacing } from "@layout";
import { forwardRef, useImperativeHandle, useRef } from "react";
import {
  Container,
  LeftContainer,
  TextInputComponent,
  TextInputContainer,
  useStyles,
} from "./styles";
import { TextInputProps, TextInputRef, TextInputType } from "./types";

export const TextInput = forwardRef<TextInputRef, TextInputProps>(
  ({ inputType = TextInputType.Primary, ...props }, _) => {
    return inputType === TextInputType.Primary ? (
      <PrimaryTextInput {...props} />
    ) : (
      <SecondaryTextInput {...props} />
    );
  },
);

const PrimaryTextInput = forwardRef<TextInputRef, TextInputProps>(
  (
    {
      label,
      placeholder = "",
      type = "text",
      onChangeText,
      value,
      error,
      leftIcon,
      onLeftIconClick,
      inputLeftIcon,
      rightIcon,
      onRightIconClick,
      onFocusCallback,
      onBlurCallback,
      disabled,
      readOnly,
      onClick,
      style,
      ...controller
    },
    ref,
  ) => {
    const styles = useStyles();

    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus() {
        inputRef.current?.focus();
      },
      blur() {
        inputRef.current?.blur();
      },
    }));

    const disabledStyle = disabled ? styles.disabled : undefined;

    return (
      <TextInputContainer
        onClick={() => {
          inputRef.current?.focus();
          onClick?.();
        }}
        style={{ ...styles.textInputContainer, ...disabledStyle, ...style }}
      >
        <Row>
          {leftIcon ? (
            <LeftContainer
              onClick={(e) => {
                e.stopPropagation();
                onLeftIconClick?.();
              }}
            >
              {leftIcon}
            </LeftContainer>
          ) : null}

          <Container onClick={onClick}>
            <Caption style={styles.label}>{label}</Caption>
            <Spacing size={SpacingSizes.XS} />
            <div style={styles.inputRow}>
              {inputLeftIcon ? (
                <>
                  {inputLeftIcon}
                  <div style={{ width: spacing.xs, flexShrink: 0 }} />
                </>
              ) : null}
              <TextInputComponent
                {...controller}
                ref={inputRef}
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={(event) => {
                  onChangeText?.(event.target.value);
                }}
                onFocus={onFocusCallback}
                onBlur={onBlurCallback}
                autoComplete="new-password"
                disabled={disabled}
                readOnly={readOnly}
                onClick={onClick}
                style={styles.primaryInput}
              />
            </div>
            <Spacing size={SpacingSizes.XXS} />

            {error ? <Caption style={styles.error}>{error}</Caption> : null}
          </Container>

          {rightIcon ? (
            <div
              onClick={(e) => {
                e.stopPropagation();
                onRightIconClick?.();
              }}
            >
              {rightIcon}
            </div>
          ) : null}
        </Row>
      </TextInputContainer>
    );
  },
);

const SecondaryTextInput = forwardRef<TextInputRef, TextInputProps>(
  (
    {
      label,
      placeholder = "",
      type = "text",
      onChangeText,
      value,
      error,
      leftIcon: _leftIcon,
      onLeftIconClick: _onLeftIconClick,
      inputLeftIcon: _inputLeftIcon,
      rightIcon: _rightIcon,
      onRightIconClick: _onRightIconClick,
      onFocusCallback,
      onBlurCallback,
      disabled,
      readOnly,
      onClick,
      style,
      ...controller
    },
    ref,
  ) => {
    const styles = useStyles();

    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus() {
        inputRef.current?.focus();
      },
      blur() {
        inputRef.current?.blur();
      },
    }));

    const disabledStyle = disabled ? styles.disabled : undefined;

    return (
      <TextInputContainer
        onClick={() => {
          inputRef.current?.focus();
          onClick?.();
        }}
        style={{ ...styles.textInputContainer, ...disabledStyle, ...style }}
      >
        <Row>
          <Container onClick={onClick}>
            <Row>
              <Title4 style={styles.secondaryLabel}>{label}</Title4>
              <TextInputComponent
                {...controller}
                ref={inputRef}
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={(event) => {
                  onChangeText?.(event.target.value);
                }}
                onFocus={onFocusCallback}
                onBlur={onBlurCallback}
                autoComplete="new-password"
                disabled={disabled}
                readOnly={readOnly}
                onClick={onClick}
                style={styles.secondaryInput}
              />
            </Row>
            {error ? (
              <>
                <Spacing size={SpacingSizes.XS} />
                <Caption style={styles.error}>{error}</Caption>
              </>
            ) : null}
          </Container>
        </Row>
      </TextInputContainer>
    );
  },
);
